import React from 'react';

const NvidiaBlackwell = () => (
  <article className="news-article-body">
    <p className="news-lead">
      Launched in Q4 2024, NVIDIA's <strong>Blackwell architecture</strong> is the silicon
      that powers the current AI frontier. The B200 GPU and the GB200 NVL72 rack deliver
      <strong>30× faster LLM inference</strong> than the H100, with 208 billion transistors
      per die, FP4 precision, and a fifth-generation NVLink interconnect that scales to
      576 GPUs acting as a single compute fabric. Blackwell is the bridge between today's
      AI and the Vera Rubin era.
    </p>

    <h2>The Blackwell Architecture — What's New</h2>

    <h3>Dual-Die Design</h3>
    <p>
      Blackwell GPUs are built on <strong>TSMC's custom 4NP process</strong> using a
      dual-reticle-limited die design: two silicon dies connected by a
      <strong>10 TB/s chip-to-chip interconnect</strong>, unified and exposed to software
      as a single GPU. This approach allows NVIDIA to pack more transistors per chip than
      any single die could accommodate — <strong>208 billion transistors</strong> per GPU.
    </p>

    <h3>Second-Generation Transformer Engine</h3>
    <p>
      The Transformer Engine has been redesigned to support new ultra-low precision formats:
    </p>
    <ul>
      <li><strong>FP4</strong>: 4-bit floating point — halves memory and compute vs FP8</li>
      <li><strong>FP8</strong>: 8-bit floating point — established in Hopper, now accelerated further</li>
      <li>Blackwell Tensor Cores deliver <strong>2× attention-layer acceleration</strong></li>
      <li><strong>1.5× more AI compute FLOPS</strong> compared to prior-generation Tensor Cores</li>
    </ul>

    <h3>Fifth-Generation NVLink</h3>
    <p>
      NVLink 5 is the interconnect that makes the "data centre as a single computer"
      philosophy real:
    </p>
    <ul>
      <li>Scales to <strong>576 GPUs</strong> in a single NVLink domain</li>
      <li><strong>130 TB/s</strong> of total GPU bandwidth in a 72-GPU NVL72 rack</li>
      <li><strong>1.8 TB/s</strong> GPU-to-GPU interconnect per link</li>
      <li>All GPUs in the domain share a unified memory address space</li>
    </ul>

    <h3>Confidential Computing</h3>
    <p>
      Blackwell is the <strong>first GPU with TEE-I/O capability</strong> (Trusted Execution
      Environment with I/O protection) — hardware-based security that encrypts data at every
      transfer point, including between CPU, GPU, and NVLink, with near-identical throughput
      to unencrypted modes. This enables regulated industries (healthcare, finance, government)
      to use GPU computing for sensitive workloads without trusting the cloud provider's
      software stack.
    </p>

    <h3>Decompression Engine</h3>
    <p>
      A dedicated on-chip decompression engine accelerates database query execution by
      <strong>18× versus CPU</strong> with <strong>5× better total cost of ownership</strong>.
      This extends Blackwell's value beyond AI training and inference into database analytics —
      a market NVIDIA is explicitly targeting with its data centre strategy.
    </p>

    <h2>The Product Line</h2>

    <h3>B200 — The Single GPU</h3>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">Transistors</span>
        <span className="news-spec-value">208 Billion</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Process</span>
        <span className="news-spec-value">TSMC 4NP (dual-die)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">AI Performance</span>
        <span className="news-spec-value">20 PetaFLOPS</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">TDP</span>
        <span className="news-spec-value">1,000W</span>
      </div>
    </div>

    <h3>GB200 NVL72 — The Rack System</h3>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">GPUs per Rack</span>
        <span className="news-spec-value">72 × B200</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">CPUs per Rack</span>
        <span className="news-spec-value">36 × Grace (ARM)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">HBM3E Memory</span>
        <span className="news-spec-value">13.5 TB</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Total Fast Memory</span>
        <span className="news-spec-value">30.5 TB</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">NVLink Bandwidth</span>
        <span className="news-spec-value">130 TB/s</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Power Draw</span>
        <span className="news-spec-value">120 kW per rack</span>
      </div>
    </div>

    <h2>Performance Gains vs H100</h2>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">LLM Inference Speed</span>
        <span className="news-spec-value">30× faster than HGX H100</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">LLM Training Speed</span>
        <span className="news-spec-value">4× faster at scale</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Database Query Speed</span>
        <span className="news-spec-value">18× faster vs CPU</span>
      </div>
    </div>

    <h2>Blackwell's Place in the Roadmap</h2>
    <p>
      Blackwell shipped in Q4 2024 and became the primary compute platform for hyperscalers
      through 2025. The GB200 NVL72 rack — combining 72 B200 GPUs with NVIDIA's own Grace
      ARM CPU — became the reference architecture for AI factories at AWS, Azure, Google
      Cloud, and Oracle. Its successor, <strong>Vera Rubin</strong> (announced CES 2026),
      promises 5× the inference performance and 10× lower cost-per-token, with H2 2026
      availability. Blackwell will remain in production and deployment for years as Vera
      Rubin phases in.
    </p>

    <h2>The Power Problem</h2>
    <p>
      At 120 kW per GB200 NVL72 rack, Blackwell introduced a new challenge: power density
      and cooling. Traditional air-cooled data centres cannot support these densities —
      liquid cooling is required for GB200 deployments. This triggered a wave of data centre
      infrastructure investment in direct liquid cooling (DLC), immersion cooling, and
      power delivery upgrades across all major hyperscaler facilities.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        Blackwell is the current state of the art in AI compute — 30× LLM inference
        acceleration, hardware-encrypted confidential computing, and a 576-GPU NVLink domain
        that erases the boundary between individual GPUs and the rack. For enterprises
        evaluating AI infrastructure, understanding Blackwell's specifications is baseline
        knowledge — it defines the performance envelope and power requirements of every
        frontier AI deployment in 2025–2026.
      </p>
    </div>
  </article>
);

export default NvidiaBlackwell;
